<route lang="json">
{
  "name": "shop-wishlist"
}
</route>

<template>
  <div>
    <DebugBlock>
      <div class="flex-row flex-align-center gap-sm">
        <Button class="grey" @click="hackTimer(5000 + 5 * 1000 * 60)">
          5 Minute Timer
        </Button>
      </div>
    </DebugBlock>
    <RepBanner />
    <div v-if="products.length === 0" class="wishlist-empty">
      <h3>Your wishlist is empty.</h3>
      <RouterLink :to="{ name: 'shop' }">
        <Button class="pink"> Start Shopping </Button>
      </RouterLink>
    </div>
    <div v-else class="wrapped-wishes flex-column">
      <Message
        v-if="error.includes('inventory-source-mismatch')"
        severity="error"
      >
        Attention: Event items and inventory items must be purchased separately.
      </Message>
      <div class="quick-add flex-row gap-2">
        <InputNumber type="number" v-model.number="qty" placeholder="Qty" />
        <Button @click.prevent="quickAdd(qty)" class="grey">Quick Add</Button>
      </div>
      <div class="product-content flex-row gap-sm">
        <div class="product-grid flex-column flex-stretch">
          <div v-for="item of products" class="item">
            <a href="#" @click.prevent="removeWishlistItem(item)" class="close">
              <i class="pi pi-times" />
            </a>
            <ProductBlock
              v-model:quantity="wishlist.items[item.id]"
              @update:quantity="
                $event <= 0 ? removeWishlistItem(item) : wishlist.save()
              "
              :object="item"
              :show-quantity="true"
              :show-wishlist="false"
              @added="handleProductAdded($event, item)"
            />
          </div>
        </div>
      </div>
      <div class="add-all flex-row flex-center">
        <Button @click.prevent="quickAdd()" class="grey">
          Add All to Bag
        </Button>
      </div>
    </div>

    <Dialog
      v-model:visible="showNotReleased"
      :dismissable-mask="true"
      :modal="true"
      :show-header="false"
    >
      <div class="show-not-released flex-column gap-sm p-lg">
        <div class="pa-p1 text-center">
          Some of your items haven’t been added because they are not available
          yet.
          <br /><br />
          Please check the countdown timer and add them to your cart when
          available.
        </div>
        <div class="flex-row flex-center">
          <Button @click="showNotReleased = false" class="grey">
            Back to Wishlist
          </Button>
        </div>
      </div>
    </Dialog>

    <Dialog
      v-model:visible="showNoZero"
      :dismissable-mask="true"
      :modal="true"
      :show-header="false"
    >
      <div class="show-no-zero">
        Please add a quantity greater than zero or click "Add All to Bag"
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import DebugBlock from "@/components/DebugBlock.vue";
import RepBanner from "@/components/rep/RepBanner.vue";
import ProductBlock from "@/components/shop/ProductBlock.vue";
import type { AddCartItem, Product } from "@/stores/defs/shop_defs";
import { useJoinStore } from "@/stores/join";
import { useShopStore } from "@/stores/shop";
import { useUserStore } from "@/stores/user";
import {
  addItemsToCart,
  type AddCartItemResult,
} from "@/stores/utils/add_to_cart";
import { useWishlistStore } from "@/stores/wishlist";
import { useHead } from "@unhead/vue";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import InputNumber from "primevue/inputnumber";
import Message from "primevue/message";
import { computed, onBeforeMount, ref } from "vue";
import { useRouter } from "vue-router";

const shop = useShopStore();
const wishlist = useWishlistStore();
const user = useUserStore();
const router = useRouter();
const join = useJoinStore();

const qty = ref(1);
const error = ref<string[]>([]);
const showNotReleased = ref(false);
const showNoZero = ref(false);

const products = computed(() => {
  const keys = Object.keys(wishlist.items);
  return shop.objects.products.filter((p) => keys.includes(`${p.id}`));
});

useHead({
  title: "Wishlist",
});

onBeforeMount(() => {
  if ((join.kit?.jewelry_count || 0) > 0) {
    router.push({ name: "shop" });
  } else if (join.kit?.id) {
    router.push({ name: "opportunities-signup" });
  }
  wishlist.clean();
});

function removeWishlistItem(item: Product) {
  const key = `${item.id}`;
  delete wishlist.items[key];
  wishlist.save();
}

async function quickAdd(quantity?: number) {
  const items: AddCartItem[] = [];
  error.value = [];
  let count = 0;

  if (quantity !== 0) {
    for (const pid in wishlist.items) {
      if (wishlist.items[pid] > 0) {
        items.push({
          product_id: parseInt(pid),
          quantity: quantity ?? wishlist.items[pid],
        });
        count += 1;
      }
    }
    const response = await addItemsToCart(items);
    if (response.ok) {
      wishlist.items = {};
    } else {
      if (Object.values(response.errors).includes("not-released")) {
        showNotReleased.value = true;
      }
      for (const item in wishlist.items) {
        if (!response.errors[item]) {
          delete wishlist.items[item];
        }
      }
    }
    wishlist.save();

    if (response.ok && !showNotReleased.value) {
      router.push({ name: "shop-cart" });
    } else if (!showNotReleased.value) {
      error.value = Object.values(response.errors);
    }
  } else {
    showNoZero.value = true;
  }
}

async function handleProductAdded(response: AddCartItemResult, item: Product) {
  if (response.ok) {
    removeWishlistItem(item);
  } else if (Object.values(response.errors).includes("not-released")) {
    showNotReleased.value = true;
  }
}

async function hackTimer(ms: number) {
  for (let x = 0; x < 2; x += 1) {
    const index = x;

    const product = shop.objects.products.find(
      (p) => p.id === products.value[index].id,
    );
    if (product) {
      product.release_date = new Date(Date.now() + ms).toISOString();
    }
  }
}
</script>

<style lang="scss" scoped>
.wrapped-wishes {
  margin: var(--1XL, 64px) 0 0 0;
  font-size: 14px;
  width: 100%;
}
.show-not-released {
  width: 286px;
  padding: 48px;
  font-size: 14px;
  font-weight: 400;
  text-align: center;
}

.quick-add {
  margin-bottom: 1em;
}
.product-grid {
  flex-wrap: wrap;
  display: grid;
  gap: var(--1XL, 64px) var(--Sm, 24px);
  grid-template-columns: repeat(4, 1fr);
}

.item {
  position: relative;
  flex: 1 1 auto;
  min-width: 100px;
  max-width: 100%;
  font-size: 14px;
  img {
    aspect-ratio: 8 / 10;
  }
}
.close {
  position: absolute;
  right: 5px;
  top: 5px;
  font-size: 2em;
  padding: 0;
  margin: 0;
  width: 32px;
  height: 32px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.pi-times {
  font-size: 14px;
}
.add-all {
  margin: var(--1XL, 64px) 0;
}

.wishlist-empty {
  min-height: 400px;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  h3 {
    font-weight: 300;
    font-size: 24px;
    margin-bottom: var(--XL, 56px);
  }
}

:deep(.add-wishlist-btn) {
  display: none;
}

.quick-add:deep(input.p-inputtext) {
  padding: var(--2XS, 8px) var(--XS, 16px);
  text-align: center;
  max-width: 80px;
}
.quick-add:deep(.p-button) {
  padding: var(--2XS, 8px) 10px;
}

toast.add {
  display: none !important;
}

.p-toast {
  display: none !important;
}

.show-no-zero {
  padding: 48px;
  max-width: 356px;
  text-align: center;
  font-weight: 400;
  font-size: 14px;
}

@media screen and (max-width: 1120px) {
  .wrapped-wishes,
  .quick-add {
    padding: 0 var(--Sm, 24px);
  }
}

@media screen and (max-width: 800px) {
  .wrapped-wishes {
    padding: 0;
  }
  .quick-add {
    justify-content: center;
  }
  :deep(.add-wishlist-btn) {
    display: block;
  }
  .product-grid {
    grid-template-columns: repeat(2, 1fr);
    row-gap: var(--XMd, 40px);
    margin: 0 auto;
  }
  .product-content {
    grid-template-columns: repeat(2, 1fr);
    row-gap: var(--XMd, 40px);
    margin: 0 auto;
  }
  .product-block {
    max-width: 262px;
  }
}

@media screen and (max-width: 391px) {
  .item {
    margin: 0 auto;
  }
}
</style>
